import React from 'react';
import { Box, Button, ButtonBase, Checkbox, FormControlLabel, IconButton, TextField, Typography } from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon, Lock as LockIcon } from '@mui/icons-material';
import FieldMappingInput from '../targets/FieldMappingInput';
import DatasetColumnSource from './DatasetColumnSource';
import { extractJsonPaths, previewValue } from '../../../../shared/utils/jsonPath';
import { COLUMN_TYPES, STANDARD_COLUMNS, resourcePathSuggestion } from '../../utils/datasetFormUtils';
import { isDatasetPath, primaryPath } from '../../utils/datasetMappingUtils';
import type { SourceForm } from '../../utils/datasetMappingUtils';
import type { ColumnRow, ColumnType, EditorMode } from '../../utils/datasetFormUtils';

interface DatasetColumnsEditorProps {
  mode: EditorMode;
  columns: ColumnRow[];
  /** The input whose sources are shown, by position. */
  input: number;
  /** The input is saved: its existing sources and its resource path are fixed. */
  inputLocked: boolean;
  keyFields: string[];
  orderingField: string;
  /** The dataset has a row path, so a plain path reads the row rather than the event envelope. */
  hasRowPath: boolean;
  /** The selected input's resource path. */
  resourcePath: string;
  /** Fixed once saved: changing it is a breaking change. */
  resourceRequired: boolean;
  /** The element a column path reads from: the first row under the row path, or the event. */
  rowSample: Record<string, unknown>;
  errors: Record<string, string>;
  disabled: boolean;
  onAdd: () => void;
  onUpdate: (index: number, changes: Partial<ColumnRow>) => void;
  onSourceChange: (index: number, source: SourceForm) => void;
  onRemove: (index: number) => void;
  onResourcePathChange: (path: string) => void;
  onResourceRequiredChange: (required: boolean) => void;
  /** Adds a column for a value of the sample row, its name and path filled in. */
  onPickSampleField: (path: string, value: unknown) => void;
}

const acceptsMappingPath = (path: string) => isDatasetPath(path, true);

const DatasetColumnsEditor: React.FC<DatasetColumnsEditorProps> = ({
  mode,
  columns,
  input,
  inputLocked,
  keyFields,
  orderingField,
  hasRowPath,
  resourcePath,
  resourceRequired,
  rowSample,
  errors,
  disabled,
  onAdd,
  onUpdate,
  onSourceChange,
  onRemove,
  onResourcePathChange,
  onResourceRequiredChange,
  onPickSampleField,
}) => {
  const editing = mode === 'edit';
  const resourceSuggestion = resourcePathSuggestion(rowSample);
  const mapped = new Set(columns.map((c) => primaryPath(c.sources[input])));
  const sampleFields = extractJsonPaths(rowSample).filter((f) => acceptsMappingPath(f.path) && !mapped.has(f.path));
  return (
    <Box>
      <Box data-testid="dataset-standard-columns" sx={{ mb: 2 }}>
        {STANDARD_COLUMNS.map((c) => (
          <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 120 }}>{c.name}</Typography>
            <Typography variant="body2" color="text.secondary">{c.type}, required, fixed</Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ mb: 2, maxWidth: 480 }}>
        <FieldMappingInput
          label="Resource path override"
          value={resourcePath}
          onChange={onResourcePathChange}
          sampleEvent={rowSample}
          disabled={disabled || inputLocked}
          emptyText="Empty reads the event's first resource"
          error={errors.resourcePath}
          pathFilter={acceptsMappingPath}
        />
        {!inputLocked && !resourcePath && resourceSuggestion && (
          <Button
            size="small"
            onClick={() => onResourcePathChange(resourceSuggestion)}
            disabled={disabled}
            aria-label={`Use ${resourceSuggestion} as the resource`}
          >
            Use {resourceSuggestion} as the resource
          </Button>
        )}
        <FormControlLabel
          sx={{ display: 'flex', mt: 0.5 }}
          label={<Typography variant="body2">Events may have no resource (account-wide)</Typography>}
          control={(
            <Checkbox
              size="small"
              checked={!resourceRequired}
              onChange={(e) => onResourceRequiredChange(!e.target.checked)}
              disabled={disabled || editing}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {columns.map((c, i) => {
          const locked = disabled || c.locked;
          const label = `Column ${i + 1}`;
          return (
            <Box key={i} data-testid={`dataset-column-${i}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  label={`${label} name`}
                  value={c.name}
                  onChange={(e) => onUpdate(i, { name: e.target.value })}
                  disabled={locked}
                  error={!!errors[`column.${i}.name`]}
                  helperText={errors[`column.${i}.name`]}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ sx: { fontFamily: 'monospace' } }}
                />
                <TextField
                  select
                  size="small"
                  label={`${label} type`}
                  value={c.type}
                  onChange={(e) => onUpdate(i, { type: e.target.value as ColumnType })}
                  disabled={locked}
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  {COLUMN_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </TextField>
                <FormControlLabel
                  label="Required"
                  control={
                    <Checkbox
                      size="small"
                      checked={c.required}
                      onChange={(e) => onUpdate(i, { required: e.target.checked })}
                      // A column added to a saved dataset must be optional: older rows lack it.
                      disabled={locked || editing}
                      inputProps={{ 'aria-label': `${label} required` }}
                    />
                  }
                />
                <Box sx={{ flex: 1 }} />
                {!c.locked && (
                  <IconButton size="small" onClick={() => onRemove(i)} disabled={disabled} aria-label={`Remove ${label.toLowerCase()}`}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <DatasetColumnSource
                label={label}
                column={c.name}
                columnType={c.type}
                source={c.sources[input]}
                locked={c.locked && inputLocked}
                place={{ identifies: keyFields.includes(c.name) || orderingField === c.name, hasRowPath }}
                mayBeNotProvided={!c.required && !keyFields.includes(c.name) && orderingField !== c.name}
                rowSample={rowSample}
                error={errors[`column.${i}.path`]}
                disabled={disabled}
                onChange={(source) => onSourceChange(i, source)}
              />
            </Box>
          );
        })}
      </Box>
      <Button size="small" startIcon={<AddIcon />} onClick={onAdd} disabled={disabled} sx={{ mt: 1 }}>
        {editing ? 'Add optional column' : 'Add column'}
      </Button>

      {sampleFields.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Or click a value of the sample row to add it as a column
          </Typography>
          <Box sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {sampleFields.map((f) => (
              <ButtonBase
                key={f.path}
                onClick={() => onPickSampleField(f.path, f.value)}
                disabled={disabled}
                aria-label={`Pick ${f.path} as a column`}
                sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 1, px: 1, py: 0.25, '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{f.path}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{previewValue(f.value, 40)}</Typography>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DatasetColumnsEditor;
